/**
 * TrialCardStep - Credit card registration step for trial onboarding.
 *
 * Renders inline (not as modal) within the OnboardingPage flow.
 * Reuses the same card form patterns from PaymentModal:
 * masks, validation, and field layout.
 *
 * SECURITY: Card data stays in local state only, sent via API, never stored.
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CreditCard, ShieldCheck } from 'lucide-react';

export interface TrialCardFormData {
  cardNumber: string;
  holderName: string;
  cpfCnpj: string;
  expMonth: string;
  expYear: string;
  cvv: string;
}

interface TrialCardStepProps {
  onSubmit: (cardData: TrialCardFormData) => Promise<void>;
  loading: boolean;
  error: string | null;
}

// ============================================================================
// Input Masks (same as PaymentModal)
// ============================================================================

function maskCardNumber(value: string): string {
  const clean = value.replace(/\D/g, '').slice(0, 16);
  return clean.replace(/(\d{4})(?=\d)/g, '$1 ');
}

function maskCpfCnpj(value: string): string {
  const clean = value.replace(/\D/g, '');
  if (clean.length <= 11) {
    return clean
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return clean
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

// ============================================================================
// Validation
// ============================================================================

interface FieldErrors {
  cardNumber?: string;
  holderName?: string;
  cpfCnpj?: string;
  expMonth?: string;
  expYear?: string;
  cvv?: string;
}

function validateField(name: string, value: string, t: (key: string) => string): string | undefined {
  switch (name) {
    case 'cardNumber': {
      const clean = value.replace(/\D/g, '');
      if (!clean) return t('plans.validation.cardRequired');
      if (clean.length < 13 || clean.length > 19) return t('plans.validation.cardInvalid');
      return undefined;
    }
    case 'holderName':
      if (!value.trim()) return t('plans.validation.nameRequired');
      if (value.trim().length < 3) return t('plans.validation.nameInvalid');
      return undefined;
    case 'cpfCnpj': {
      const clean = value.replace(/\D/g, '');
      if (!clean) return t('plans.validation.cpfCnpjRequired');
      if (clean.length !== 11 && clean.length !== 14) return t('plans.validation.cpfCnpjInvalid');
      return undefined;
    }
    case 'expMonth': {
      const month = parseInt(value, 10);
      if (!value || isNaN(month) || month < 1 || month > 12) return t('plans.validation.monthInvalid');
      return undefined;
    }
    case 'expYear': {
      if (!value || !/^\d{2}$/.test(value)) return t('plans.validation.yearInvalid');
      return undefined;
    }
    case 'cvv':
      if (!value || !/^\d{3,4}$/.test(value)) return t('plans.validation.cvvInvalid');
      return undefined;
    default:
      return undefined;
  }
}

// ============================================================================
// Component
// ============================================================================

const TrialCardStep: React.FC<TrialCardStepProps> = ({ onSubmit, loading, error }) => {
  const { t } = useTranslation();

  const [form, setForm] = useState<TrialCardFormData>({
    cardNumber: '',
    holderName: '',
    cpfCnpj: '',
    expMonth: '',
    expYear: '',
    cvv: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});

  const handleChange = useCallback((name: keyof TrialCardFormData, raw: string) => {
    let value = raw;
    if (name === 'cardNumber') value = maskCardNumber(raw);
    else if (name === 'cpfCnpj') value = maskCpfCnpj(raw);
    else if (name === 'expMonth') value = raw.replace(/\D/g, '').slice(0, 2);
    else if (name === 'expYear') value = raw.replace(/\D/g, '').slice(0, 2);
    else if (name === 'cvv') value = raw.replace(/\D/g, '').slice(0, 4);

    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: FieldErrors = {};
    for (const key of Object.keys(form) as (keyof TrialCardFormData)[]) {
      const err = validateField(key, form[key], t);
      if (err) newErrors[key] = err;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await onSubmit({
      ...form,
      cardNumber: form.cardNumber.replace(/\s/g, ''),
      cpfCnpj: form.cpfCnpj.replace(/\D/g, ''),
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded-lg bg-primary/10">
          <CreditCard className="h-5 w-5 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {t('onboardingSurvey.trialStep.title')}
        </h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {t('onboardingSurvey.trialStep.subtitle')}
      </p>

      {/* Card Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Card Number */}
        <div>
          <Label className="text-xs text-muted-foreground mb-1">{t('plans.payment.cardNumber')}</Label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="0000 0000 0000 0000"
            value={form.cardNumber}
            onChange={(e) => handleChange('cardNumber', e.target.value)}
            disabled={loading}
            className={errors.cardNumber ? 'border-destructive' : ''}
          />
          {errors.cardNumber && <p className="text-xs text-destructive mt-1">{errors.cardNumber}</p>}
        </div>

        {/* Holder Name */}
        <div>
          <Label className="text-xs text-muted-foreground mb-1">{t('plans.payment.holderName')}</Label>
          <Input
            type="text"
            placeholder="Nome completo"
            value={form.holderName}
            onChange={(e) => handleChange('holderName', e.target.value)}
            disabled={loading}
            className={errors.holderName ? 'border-destructive' : ''}
          />
          {errors.holderName && <p className="text-xs text-destructive mt-1">{errors.holderName}</p>}
        </div>

        {/* CPF/CNPJ */}
        <div>
          <Label className="text-xs text-muted-foreground mb-1">{t('plans.payment.cpfCnpj')}</Label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="000.000.000-00"
            value={form.cpfCnpj}
            onChange={(e) => handleChange('cpfCnpj', e.target.value)}
            disabled={loading}
            className={errors.cpfCnpj ? 'border-destructive' : ''}
          />
          {errors.cpfCnpj && <p className="text-xs text-destructive mt-1">{errors.cpfCnpj}</p>}
        </div>

        {/* Expiry + CVV Row */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground mb-1">{t('plans.payment.month')}</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="MM"
              value={form.expMonth}
              onChange={(e) => handleChange('expMonth', e.target.value)}
              disabled={loading}
              className={errors.expMonth ? 'border-destructive' : ''}
            />
            {errors.expMonth && <p className="text-xs text-destructive mt-1">{errors.expMonth}</p>}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1">{t('plans.payment.year')}</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="AA"
              value={form.expYear}
              onChange={(e) => handleChange('expYear', e.target.value)}
              disabled={loading}
              className={errors.expYear ? 'border-destructive' : ''}
            />
            {errors.expYear && <p className="text-xs text-destructive mt-1">{errors.expYear}</p>}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1">{t('plans.payment.cvv')}</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="000"
              value={form.cvv}
              onChange={(e) => handleChange('cvv', e.target.value)}
              disabled={loading}
              className={errors.cvv ? 'border-destructive' : ''}
            />
            {errors.cvv && <p className="text-xs text-destructive mt-1">{errors.cvv}</p>}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Terms */}
        <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
          <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">
            {t('onboardingSurvey.trialStep.terms')}
          </p>
        </div>

        {/* Submit */}
        <Button type="submit" className="w-full h-11" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('onboardingSurvey.trialStep.processing')}
            </>
          ) : (
            t('onboardingSurvey.trialStep.submit')
          )}
        </Button>
      </form>
    </div>
  );
};

export default TrialCardStep;
